import { AfterViewInit, ChangeDetectionStrategy, Component, inject, OnInit, Renderer2 } from '@angular/core';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { Title } from '@angular/platform-browser';
import {
    ActivatedRoute,
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    RouteConfigLoadStart,
    Router,
} from '@angular/router';
import { TokenStorage } from '@app/core/token.storage';
import { AppBreadcrumbsService } from '@app/layout/breadcrumbs/services/app-breadcrumbs.service';
import { SocketsService } from '@fui/fui-sockets';
import { FuiDestroyService } from '@services/destroy.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { filter, map, mergeMap, takeUntil } from 'rxjs/operators';
import { getSpinnerHtml } from 'src/shared/helpers/spinner-helpers';
import { AppService } from './app.service';
import { SideMenuService } from './layout/side-menu/services/side-menu.service';

/** Главный компонент системы */
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
})
export class AppComponent implements OnInit, AfterViewInit {
    readonly #token = inject(TokenStorage);
    readonly #spinner = inject(NgxSpinnerService);
    readonly #destroy$ = inject(FuiDestroyService);
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);
    private titleService = inject(Title);
    public sideMenuService = inject(SideMenuService);
    public appService = inject(AppService);
    private _media = inject(MediaObserver);
    private breadcrumbsService = inject(AppBreadcrumbsService);
    private renderer = inject(Renderer2);
    private readonly socketsService = inject(SocketsService);

    /** Спиннер */
    readonly spinnerHtml = getSpinnerHtml();

    constructor() {
        this.socketsService.runInitializers();
    }

    /** Событие окончания инициализации компонента */
    ngOnInit() {
        this.appService.isPublic = this.#token.getToken() === null;

        // Установка фавикона
        if (this.appService.getFaviconPatch()) {
            const header = document.getElementById('favicon') as HTMLAnchorElement;
            header.href = this.appService.serverUrl + this.appService.getFaviconPatch();
        }
        const body = document.querySelector('.mat-typography');

        if (body) {
            body.setAttribute('data-theme', this.appService.getSettingsByKey('system_color'));
            body.classList.toggle('public-page', this.appService.isPublic);
        }

        // Смотрим на все события роутера
        this.router.events
            .pipe(
                // Фильтруем по событию NavigationEnd
                filter((event) => event instanceof NavigationEnd),
                // Переключаем поток на сервис activatedRoute
                map(() => this.activatedRoute),
                // Нас интересуют только корневые роуты (это опционально)
                map((route) => {
                    while (route.firstChild) {
                        route = route.firstChild;
                    }
                    return route;
                }),
                // Так же мы выбираем только первичные аутлеты (тоже опционально)
                filter((route) => route.outlet === 'primary' || route.outlet === 'public'),
                // Выбираем title
                mergeMap((route) => route.data),
                takeUntil(this.#destroy$),
            )
            .subscribe((event) => {
                this.appService.isPublic = event.is_public;
                body.classList.toggle('public-page', this.appService.isPublic);
                this.appService.link = this.router.url.startsWith('/')
                    ? this.router.url.slice(1, this.router.url.length)
                    : this.router.url;
                this.appService.routeData = event;

                // Установка хлебных крошек
                if (this.sideMenuService?.menuDataSource?.data) {
                    this.breadcrumbsService.setBreadcrumbs(
                        this.sideMenuService.setActiveMenuItem(this.appService.link),
                    );
                    const data = this.breadcrumbsService.getItemByIndex(-1);
                    this.appService.sideMenuTitle = data?.name ?? '';
                    this.titleService.setTitle(data?.name ?? this.appService.getSettingsByKey('header_big_text'));
                } else {
                    this.titleService.setTitle(this.appService.getSettingsByKey('header_big_text'));
                }
            });

        // При начале lazy-loading подгрузки модуля показываем прелоадер
        this.router.events
            .pipe(
                filter((event) => event instanceof RouteConfigLoadStart),
                takeUntil(this.#destroy$),
            )
            .subscribe(() => this.#spinner.show());

        // При завершении навигации скрываем прелоадер
        this.router.events
            .pipe(
                filter(
                    (event) =>
                        event instanceof NavigationEnd ||
                        event instanceof NavigationCancel ||
                        event instanceof NavigationError,
                ),
                takeUntil(this.#destroy$),
            )
            .subscribe(() => this.#spinner.hide());

        // Подписка на изменение ширины экрана
        this._media
            .asObservable()
            .pipe(takeUntil(this.#destroy$))
            .subscribe((val: MediaChange[]) => {
                switch (val[1].mqAlias) {
                    case 'gt-sm':
                    case 'lt-md':
                    case 'lt-sm':
                        this.appService.showSideMenu.next(false);
                        break;
                    default:
                        this.appService.showSideMenu.next(true);
                }
            });
    }

    /** Событие после отрисовки компонента */
    ngAfterViewInit(): void {
        try {
            this.renderer.selectRootElement('#appRootPreloader').style.display = 'none';
            this.addPWAFeatures();
        } catch (err: unknown) {
            console.warn(err);
        }
    }

    /** Получить flex для меню закрытой части системы */
    public getAdminMenuFlex(): string {
        if (this._media.isActive('gt-sm')) {
            if (this.appService.settings?.is_fixed_sidenav) {
                return '0 0 16rem';
            } else {
                return '0 0 3rem';
            }
        } else {
            return '0 0 0px';
        }
    }

    /** Получить flex для основной зоны закрытой части системы */
    public getAdminContentFlex(): string {
        if (this._media.isActive('gt-sm')) {
            if (this.appService.settings?.is_fixed_sidenav) {
                return '0 0 calc(100% - 16rem)';
            } else {
                return '0 0 calc(100% - 3rem)';
            }
        } else {
            return '0 0 100%';
        }
    }

    /** Добавить манифест и скрипты для PWA если оно включено */
    private addPWAFeatures() {
        if (!!this.appService._config.settings.enable_pwa) {
            const manifest = this.renderer.createElement('link');
            manifest.rel = 'manifest';
            manifest.href = '/api/pwa/manifest';
            this.renderer.appendChild(document.head, manifest);

            const script = this.renderer.createElement('script');
            script.async = true;
            script.src = '/assets/pwa/init.js';
            this.renderer.appendChild(document.body, script);

            const meta = this.renderer.createElement('meta');
            meta.name = 'theme-color';
            meta.content = this.appService._config.settings.theme_color ?? '#1d4797';
            this.renderer.appendChild(document.head, meta);
        }
    }
}
